import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { API_BASE } from '../config/api';
import analyticsTracker from '../utils/analyticsTracker';

const SpecialOfferModal = ({ offer, parkName, onClose, isAdmin = false, onEditClick }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSendOffer = async () => {
    if (!email || !/^[\w\.-]+@[\w\.-]+\.\w+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the terms & conditions');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_BASE}/special-offers/email`, {
        offer_id: offer.id,
        recipient_email: email
      });
      
      // Track analytics: email request
      analyticsTracker.trackEmailRequest(offer.park_id);
      
      // Show success modal instead of just toast
      toast.success('🎉 Offer sent to your email! Check your inbox.');
      
      // Close modal after brief delay
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      console.error('Failed to send offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSending(false);
    }
  };

  const businessName = offer.business_name || offer.offer_title || parkName;

  return (
    <>
      {/* Main Offer Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header with Happy Paws Branding */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 rounded-t-xl">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">🐾 Special Offer</h2>
                <p className="text-white text-opacity-90 font-medium">{businessName}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 text-3xl leading-none"
              >
                ×
              </button>
            </div>
          </div>

          {/* Offer Content - Renders Sanitized Rich HTML */}
          <div className="p-6">
            <div 
              className="prose prose-sm max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(offer.offer_description) }}
            />

            {/* Terms & Conditions - Always Show */}
            <div className="mb-6">
              <button
                onClick={() => setShowTermsModal(true)}
                className="text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-md transition-colors font-medium"
              >
                Terms
              </button>

              {/* Terms Acceptance Checkbox - Required Field */}
              <label className="flex items-start gap-3 mt-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  required
                />
                <span className="text-sm text-gray-700">
                  I understand the Terms
                </span>
              </label>
            </div>

            {/* Footer - User Controls (visible to all) + Admin Controls */}
            <div className="border-t border-gray-200 pt-6">
              {/* User Controls - Email Input and Send Button (visible to all) */}
              <>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📧 Email address to receive voucher:
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 mb-4"
                />

                {/* Send Offer Button */}
                <button
                  onClick={handleSendOffer}
                  disabled={sending || !email || !termsAccepted}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending to your email...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Offer
                    </>
                  )}
                </button>
              </>

              {/* Admin Controls - Additional Edit Button */}
              {isAdmin && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-blue-800 font-medium text-sm flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Admin: You can test the offer flow or edit it below
                    </p>
                  </div>
                  
                  <button
                    onClick={onEditClick}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Go to Edit Screen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="bg-gray-100 p-4 rounded-t-lg flex justify-between items-center border-b">
              <h3 className="text-lg font-bold text-gray-800">Terms & Conditions</h3>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {offer.terms || 'No terms specified for this offer.'}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-b-lg border-t">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SpecialOfferModal;
